/**
 * BL-1286 (ADR 033) — `canApplyAuctionTransition`: the auction twin of
 * `canApplyLotTransition`, keyed on the ADR-023 derived {@link AuctionPhaseKind}
 * × the requested {@link AuctionTransition}. It is the encoding of §4 of the
 * signed `docs/feature/admin/lifecycle-transition-matrix.md` grid, plus the
 * cross-entity invariants I1/I2/I3 from §2.
 *
 * **Why this exists.** Auction eligibility was spread across three sources that
 * did not know about each other — the since-deleted `auction-status-validation.ts`
 * (a lifecycle rank table + "blocked if bids exist", retired in BL-1289),
 * `auction-ready-gate.ts` (the go-live blockers), and `SoftDeleteService` (the
 * restrict/guard blockers). Nothing could answer "what can I do to this auction
 * right now, and why not?" in one call, which is precisely why there was no
 * auction status control. This guard is that one answer, and as of BL-1289 the
 * admin settings status control consumes it.
 *
 * Pure IsomorphicService (ADR 010 / sharing.md §3): no I/O, no auth, no stamp
 * writes. Client-safe and `useMemo`-safe, so the BL-1292 dropdown can pre-disable
 * ineligible rows without a round trip.
 *
 * **Cross-entity facts are passed IN, never fetched.** I1 (open lots), the
 * go-live gate, and the soft-delete restrict blockers all need rows this
 * function must not read. The caller (the check API's service) resolves them and
 * hands them over as plain counts, keeping the guard pure and free of any app
 * import. Callers that omit them get the phase-axis answer only.
 *
 * **Scope — the whole §4 grid is decided (BL-1292).** Every transition in
 * {@link AuctionTransition} is answered here. The last two holdouts,
 * `open-pre-bidding` (§4.3) and `mark-pre-bidding` (§4.3b), used to **throw**
 * {@link TransitionNotImplementedError}: BL-1291 gave them a representable
 * destination (`accepting-pre-bids`), but their effects were still undeclared on
 * the transition engine. BL-1292 declared those effects and implements the two
 * cells, so `IMPLEMENTED_TRANSITIONS` now covers the union and nothing throws in
 * practice. The `default:` arm below is kept as the compile-time exhaustiveness
 * proof — adding a transition to the union without deciding it here is a `tsc`
 * error, and a silent `{ allowed: true }` on an unbuilt path stays impossible.
 *
 * Why this mattered: `LIVE_AUCTION_TRANSITIONS` filters the catalog on `status`,
 * so while these two threw, the status dropdown could not offer pre-bidding at
 * all — the settings control had to answer `accepting-pre-bids` by BYPASSING the
 * guard, which is precisely the second decision-maker rule #38 §2 bans. Deciding
 * them here is what let that bypass be deleted.
 *
 * **This guard is now the single source of truth for backward eligibility.** It
 * returns `allowed` for backward transitions on a bid-bearing auction (matrix
 * §4.1, changed 2026-08-18: "a bid-bearing auction may move back"). BL-1289
 * closed the divergence this comment used to record: `auction-status-validation.ts`
 * — which held `isAuctionRevertBlockedByBids` and blocked exactly that write —
 * was DELETED, and the admin settings status control now derives its per-target
 * availability from this function. Nothing re-decides "can this move?" elsewhere;
 * a caller that does is a finding (rule #38 §2).
 */
import type { AuctionPhaseKind } from './phase-tokens';
import { TransitionNotImplementedError } from './lot-transition-guard';

/**
 * The ADR-033 auction transition catalog (matrix §4.0). Every cell of the grid
 * resolves to one of these.
 */
export type AuctionTransition =
  | 'mark-draft'
  | 'mark-ready'
  | 'open-pre-bidding'
  | 'mark-pre-bidding'
  | 'open'
  | 'pause'
  | 'resume'
  | 'close'
  /** BL-2279 (PRJ-89, matrix §4.7b) — move a closed auction through its post-close stages. */
  | 'set-close-stage'
  | 'reopen'
  | 'soft-delete'
  | 'restore';

/**
 * Why an auction transition was refused (matrix §4.0 "Why each ✕ exists", plus
 * the precondition gates). Deliberately its OWN vocabulary rather than a reuse
 * of `BatchBlockReason`, which is documented as the per-LOT batch axis and
 * carries lot-only reasons (`sold-lock`, `ceiling`, …) that can never apply to
 * an auction.
 */
export type AuctionBlockReason =
  /** The auction is soft-deleted; restore it before anything else. */
  | 'deleted'
  /** Already in the target phase — the transition is a no-op. */
  | 'already-there'
  /** Matrix "not running": nothing to pause/resume. */
  | 'not-pausable'
  /** Not closed, so there is nothing to un-close. */
  | 'not-reopenable'
  /**
   * BL-2278 — Closed, but `opened_at` was never stamped (the auction was closed
   * from a pre-open phase). Un-closing it would land it pre-open with no go-live
   * gate, so the operator moves it to Draft or Upcoming instead.
   */
  | 'never-opened'
  /**
   * BL-2279 — only a closed auction has post-close stages to move through. Its
   * own reason rather than `not-reopenable`, whose copy names the wrong move.
   */
  | 'not-closed'
  /**
   * BL-2281 (PRJ-89 D6) — `set-close-stage` to a stage the house has not named:
   * the target is above the house's configured stage count, or the house names
   * no stages at all. `AUCTION_CLOSE_STAGE_COUNT` is only the platform maximum.
   */
  | 'stage-not-configured'
  /** The phase cannot reach the open transitions (matrix §4.4 `not-openable`). */
  | 'not-openable'
  /** Invariant I1 — a Closed auction may not sit above non-terminal lots. */
  | 'open-lots'
  /** The go-live gate (`getAuctionReadyBlockers`) reported unmet requirements. */
  | 'not-ready'
  /**
   * Matrix §4.3's precondition — `open-pre-bidding` sweeps every `ready` lot,
   * and this auction has none, so there is nothing to open. Distinct from
   * `not-ready` (which is about the AUCTION's go-live requirements) because the
   * operator's fix is different: add or ready some lots, not fill in the terms.
   */
  | 'no-eligible-lots'
  /** `SoftDeleteService` restrict/guard blockers exist (lots, bids, financials). */
  | 'delete-blocked'
  /**
   * BL-2280 (PRJ-89 D3, matrix §7al) — the auction sits at a post-close stage
   * (`currentCloseStage > 0`). Every exit from Closed (reopen, Draft, Upcoming,
   * Delete) waits until it is moved back to plain Closed, so an auction never
   * leaves a settlement stage by a side door. Outranks the reason each exit
   * already had.
   */
  | 'close-stage-set'
  /** Only a soft-deleted auction can be restored. */
  | 'not-restorable';

/** The verdict. Mirrors `BatchApplyVerdict`'s shape so both guards read alike. */
export type AuctionApplyVerdict =
  | { allowed: true }
  | { allowed: false; reason: AuctionBlockReason };

/**
 * Phases `mark-draft` may be reached FROM (matrix §4.1). Everything except
 * `draft` itself (a no-op) and `deleted`.
 */
const MARK_DRAFT_SOURCES = new Set<AuctionPhaseKind>([
  // BL-1291: §4.1 is "everything except self and deleted" — the new phase is
  // covered by that existing rule, not a new cell. AC 3's back-to-Upcoming.
  'accepting-pre-bids',
  'ready',
  'active',
  'paused',
  'closed',
]);

/**
 * Phases `mark-ready` may be reached FROM (matrix §4.2). Everything except
 * `ready` itself (a no-op) and `deleted`.
 */
const MARK_READY_SOURCES = new Set<AuctionPhaseKind>([
  'draft',
  // BL-1291: same §4.2 "everything except self and deleted" rule. This is the
  // move that STOPS pre-bidding — one action on the auction, no lot writes.
  'accepting-pre-bids',
  'active',
  'paused',
  'closed',
]);

/**
 * Phases `open` may be reached FROM (matrix §4.4). BL-1291 added
 * `accepting-pre-bids` as this comment anticipated: opening for bidding is what
 * ENDS pre-bidding, so there is no separate "stop pre-bidding" step (AC 2).
 */
const OPEN_SOURCES = new Set<AuctionPhaseKind>(['ready', 'accepting-pre-bids']);

/**
 * Phases `open-pre-bidding` may be reached FROM (matrix §4.3) — `ready` alone.
 *
 * BL-1292: the forward transition SWEEPS every `ready` lot (stamping
 * `pre_bidding_at` + `readied_at` and populating close times), so it only makes
 * sense from the one phase that has a `ready` lot set to sweep. A phase that
 * already ran has nothing to enrol, and enrolling lots there would auto-open
 * lots that were never in pre-bidding — invariant I4.
 */
const OPEN_PRE_BIDDING_SOURCES = new Set<AuctionPhaseKind>(['ready']);

/**
 * Phases `mark-pre-bidding` may be reached FROM (matrix §4.3b) — the BACKWARD
 * entry, allowed from the two RUNNING phases.
 *
 * BL-1292: deliberately NOT reachable from `draft` (§4.3b: "use `mark-ready` +
 * `open-pre-bidding`") or from `ready` (from there the forward move IS
 * `open-pre-bidding`). This transition writes the auction stamp and NOTHING
 * else — that is what makes the walk-back lossless (invariant I4a): clearing
 * `opened_at` lets every lot still holding `pre_bidding_at` re-derive
 * `pre-bidding` on its own.
 *
 * **BL-1559 (§7o) removed `post-sale` and `closed`.** They made this the one
 * door into a bid-accepting phase that consulted no gate at all — not the
 * go-live completeness checks, not the terms check — so a finished auction
 * could be put back on the block in a single dropdown pick with a historical
 * schedule or no terms & conditions attached. BL-1432 closed exactly that hole
 * on the FORWARD door (`open-pre-bidding`, §4.3) and left this backward twin,
 * which lands in the identical phase, open.
 *
 * Closed by TOPOLOGY rather than by a precondition: with only running auctions
 * as sources, every path into `accepting-pre-bids` from a non-running auction
 * now runs through `ready`, which IS gated. Adding the go-live gate to this
 * cell instead would have been the WRONG instrument — an Active auction's
 * `first_lot_close_at` is legitimately in the past the moment its first lot
 * closes, so gating here would break BL-1291's lossless Active → Accepting
 * pre-bids walk-back (its AC 7/AC 8; BL-1559 RAC 1).
 *
 * Keep in lockstep with `AUCTION_TRANSITION_CATALOG['mark-pre-bidding'].from`.
 */
const MARK_PRE_BIDDING_SOURCES = new Set<AuctionPhaseKind>(['active', 'paused']);

/** Transitions this guard decides. BL-1292 completed the set — the two
 *  pre-bidding entries joined once their effects were declared on the engine,
 *  so this is now the whole {@link AuctionTransition} union. */
const IMPLEMENTED_TRANSITIONS = new Set<AuctionTransition>([
  'mark-draft',
  'mark-ready',
  'open-pre-bidding',
  'mark-pre-bidding',
  'open',
  'pause',
  'resume',
  'close',
  'set-close-stage',
  'reopen',
  'soft-delete',
  'restore',
]);

/**
 * Decide whether an auction in its current derived phase may make the requested
 * lifecycle transition.
 *
 * @param input.auctionPhase The auction's ADR-023 derived phase (from
 *   `deriveAuctionPhaseKind` or `deriveAuctionPhase`). Typed on the minimal
 *   `{ kind }` shape (sharing.md §3 rule 2) — a full phase object satisfies it
 *   structurally.
 * @param input.transition The requested transition.
 * @param input.openLotCount Count of non-deleted, non-terminal lots under the
 *   auction. Invariant I1: `close` is blocked while this is above zero. Omitted
 *   ⇒ treated as `0` (phase-axis-only answer).
 * @param input.readyBlockerCount Number of unmet go-live requirements from
 *   `getAuctionReadyBlockers`. Gates the THREE publishing transitions
 *   (`mark-ready`, `open`, and — since BL-1432's M6 amendment —
 *   `open-pre-bidding`; matrix §4.2's "generalized, not invented" note).
 *   Omitted ⇒ `0`.
 *   The blocker MESSAGES are the caller's to surface; the guard only needs to
 *   know whether any exist.
 * @param input.softDeleteBlocked Whether `SoftDeleteService` reported restrict
 *   blockers. `soft-delete` is refused when true. Omitted ⇒ `false`.
 * @param input.noEligibleLots Whether the auction has NO lot `open-pre-bidding`
 *   would sweep (matrix §4.3's precondition). Shaped as a blocker boolean rather
 *   than a count so its omitted default is the permissive one, matching
 *   `softDeleteBlocked`. Omitted ⇒ `false` (phase-axis-only answer).
 * @param input.hasOpened Whether `auctions.opened_at` is set (BL-2278). `reopen`
 *   is refused `never-opened` when false. Omitted ⇒ `true` (phase-axis-only
 *   answer), so the write path and the check API must pass it.
 * @param input.currentCloseStage BL-2279 — the closed auction's derived stage
 *   (`deriveCloseStage`). Read by `set-close-stage`, and (BL-2280) by the four
 *   exits from Closed — `reopen`, `mark-draft`, `mark-ready`, `soft-delete` —
 *   which are refused `close-stage-set` while it is above 0. Omitted ⇒ the
 *   answer comes from the phase alone (the catalog replay).
 * @param input.targetCloseStage BL-2279 — the stage `set-close-stage` moves to.
 *   Equal to `currentCloseStage` ⇒ `already-there`. Omitted ⇒ phase alone.
 * @param input.configuredCloseStageCount BL-2281 — how many closing stages the
 *   house names (its non-null stage labels). Read by `set-close-stage` only: a
 *   target above it is refused `stage-not-configured`, and a house naming none
 *   refuses every stage move, the target-less check-path ask included. Omitted ⇒
 *   no house bound (the catalog replay), so the write path and the check API
 *   must pass it.
 * @returns {@link AuctionApplyVerdict}.
 * @throws {TransitionNotImplementedError} never in practice — every member of
 *   {@link AuctionTransition} is implemented as of BL-1292. Retained as the
 *   compile-time exhaustiveness guard for a future transition.
 * @sideEffect none — pure.
 */
export function canApplyAuctionTransition(input: {
  auctionPhase: { kind: AuctionPhaseKind };
  transition: AuctionTransition;
  openLotCount?: number;
  readyBlockerCount?: number;
  softDeleteBlocked?: boolean;
  noEligibleLots?: boolean;
  hasOpened?: boolean;
  currentCloseStage?: number;
  targetCloseStage?: number;
  configuredCloseStageCount?: number;
}): AuctionApplyVerdict {
  const {
    auctionPhase,
    transition,
    openLotCount = 0,
    readyBlockerCount = 0,
    softDeleteBlocked = false,
    noEligibleLots = false,
    hasOpened = true,
    currentCloseStage,
    targetCloseStage,
    configuredCloseStageCount,
  } = input;
  const { kind } = auctionPhase;

  if (!IMPLEMENTED_TRANSITIONS.has(transition)) {
    throw new TransitionNotImplementedError(transition, 'canApplyAuctionTransition');
  }

  const block = (reason: AuctionBlockReason): AuctionApplyVerdict => ({
    allowed: false,
    reason,
  });
  // BL-2280 (D3, §7al) — explicit `> 0`, never truthiness or `!= null`: stage 0
  // is plain Closed and must answer exactly as it did before (AC 5), and an
  // omitted stage is the phase-only answer.
  const staged = currentCloseStage != null && currentCloseStage > 0;

  // `restore` is the ONLY transition a soft-deleted auction may make, so it is
  // decided before the global deleted-immutability check below (the same
  // ordering the lot guard uses for `reopen`).
  if (transition === 'restore') {
    return kind === 'deleted' ? { allowed: true } : block('not-restorable');
  }

  // Matrix §4: every row but `deleted` has live cells; the `deleted` row offers
  // `restore→` and nothing else.
  if (kind === 'deleted') {
    return block('deleted');
  }

  switch (transition) {
    // §4.1 — no preconditions. A bid-bearing auction MAY move back, and so may a
    // Closed one (`statusDecisionStampSet` clears `closed_at` for any non-Closed
    // target, so no separate un-close step is needed).
    //
    // BL-2280 — except at a post-close stage: the move would clear `closed_at`
    // and drop the auction out of its settlement stage.
    case 'mark-draft':
      if (!MARK_DRAFT_SOURCES.has(kind)) return block('already-there');
      return staged ? block('close-stage-set') : { allowed: true };

    // §4.2 — gated by the go-live gate, because marking Ready publishes.
    // BL-2280 — the stage fence answers BEFORE the gate: no terms fix can move a
    // staged auction, so naming one would send the operator the wrong way.
    case 'mark-ready':
      if (!MARK_READY_SOURCES.has(kind)) return block('already-there');
      if (staged) return block('close-stage-set');
      return readyBlockerCount > 0 ? block('not-ready') : { allowed: true };

    // §4.4 — opening publishes too, so it carries the same go-live gate.
    case 'open':
      if (!OPEN_SOURCES.has(kind)) return block('not-openable');
      if (readyBlockerCount > 0) return block('not-ready');
      // BUG-1479 (matrix §7q) — the lot precondition §4.3 has always had and
      // this arm never did. Until now `open` had NO lot check in the guard at
      // all: an auction passing the go-live gate with ZERO lots went live over
      // an empty catalog, published its snapshot, and showed on the bidder site
      // as an open sale with nothing in it. Reproduced against a running app —
      // the check API answered `available: true` and a direct POST returned 200
      // and stamped `opened_at`.
      //
      // The write path's only lot-related refusal is gated on `blockedCount > 0`
      // (`open-timed-auction-service.ts`), and that counts lots that EXIST but
      // are missing an openability field — so the zero-lot case sailed past it.
      //
      // Gate AFTER `readyBlockerCount`, exactly as `open-pre-bidding` below
      // does: when both are unmet the go-live failure is the more fundamental
      // one, and once it clears the operator gets the lot reason, whose fix is a
      // different action.
      return noEligibleLots ? block('no-eligible-lots') : { allowed: true };

    // §4.3 — the FORWARD entry into pre-bidding. Only from `ready`, and only
    // when there is something to sweep: the transition enrols every `ready` lot,
    // so an auction with none would stamp `pre_bidding_at` and light up a
    // pre-bidding phase over an empty catalog.
    case 'open-pre-bidding':
      if (!OPEN_PRE_BIDDING_SOURCES.has(kind)) return block('not-openable');
      // BL-1432 (M6, §7i) — pre-bidding publishes, so it carries the SAME
      // go-live gate `mark-ready` and `open` already do. Until M6 this
      // transition checked only the lot count, so an auction failing the
      // completeness checks could start accepting real advance bids from real
      // buyers through a side door. The gate is re-evaluated live, which is
      // what makes it reachable: terms or a close time can be cleared after an
      // auction reaches `ready`, and the close-after-start ordering blocker is
      // server-reachable regardless of how it got there.
      //
      // Gate BEFORE the lot precondition, deliberately. When both are unmet the
      // go-live failure is the more fundamental one: an auction that cannot
      // legally go live should be told to fix that, not sent off to ready some
      // lots it still could not open. AC 4's distinction is preserved by the
      // ordering — once the gate is clear this falls through and the operator
      // gets the lots reason, whose fix is a different action.
      if (readyBlockerCount > 0) return block('not-ready');
      // Phase before precondition: a Draft auction with no lots fails on the
      // PHASE, and telling the operator to add lots would be the wrong fix.
      return noEligibleLots ? block('no-eligible-lots') : { allowed: true };

    // §4.3b — the BACKWARD entry. No preconditions, and BL-1559 (§7o) replaced
    // the reason this comment used to give.
    //
    // It said: "it writes the auction stamp and no lot row, so there is nothing
    // to be eligible or ineligible." That is an argument about *lot*
    // eligibility, and it was silently doing duty for everything else — which
    // is how this became the one door into a bid-accepting phase that checked
    // neither the go-live gate nor the terms.
    //
    // The real reason there is no precondition: {@link MARK_PRE_BIDDING_SOURCES}
    // is now the two RUNNING phases, and an auction only reaches those by
    // passing the go-live gate at `open`. The sources ARE the precondition.
    //
    // The self-case is called out separately because §4.3b excludes FIVE phases
    // and `already-there` is only honest for one of them.
    case 'mark-pre-bidding':
      if (kind === 'accepting-pre-bids') return block('already-there');
      return MARK_PRE_BIDDING_SOURCES.has(kind) ? { allowed: true } : block('not-openable');

    // §4.5 — pause requires a running auction. `ready` is NOT running (AC 5).
    case 'pause':
      return kind === 'active' ? { allowed: true } : block('not-pausable');

    // §4.6 — resume is the exact inverse; only a paused auction has clocks to
    // restart.
    case 'resume':
      return kind === 'paused' ? { allowed: true } : block('not-pausable');

    // §4.7 — allowed from every live phase, and decided by invariant I1: no
    // Closed auction above non-terminal lots.
    //
    // BL-2278 (matrix §7, Michael Lawrie 2026-10-06) added `draft`, `ready` and
    // `accepting-pre-bids`. Until then only the phases that ran (`active` /
    // `paused`) could close, which made Closed → Draft / Upcoming a one-way
    // trap: the backward move clears `opened_at`, the lots stay terminal, and
    // no door led back to Closed. I1 keeps the widening honest — a pre-open
    // auction closes only once every lot is finished, which is exactly that
    // recovery state.
    //
    // Consequence: `closed` no longer implies `opened_at`. `reopen` below
    // refuses such an auction, and the lot landing gate takes
    // `parentAuctionHasOpened` for the same reason.
    case 'close':
      if (kind === 'closed') return block('already-there');
      return openLotCount > 0 ? block('open-lots') : { allowed: true };

    // §4.7b (BL-2279) — closed → closed. The phase decides first, so a Ready
    // auction hears "close it first", never "already there". Then a target equal
    // to the current stage is a no-op; any other stage, forward or backward, is a
    // legal move. With the stage params omitted the answer is the phase alone.
    // BL-2281: last, the house's configured count bounds the target.
    // BL-2282 carry-in (P3 from #6249): the target-less ask on a STAGED auction
    // is allowed before that bound — moving back to stage 0 is always legal, so
    // a house whose stages were since un-configured is never stranded.
    case 'set-close-stage':
      if (kind !== 'closed') return block('not-closed');
      if (currentCloseStage != null && currentCloseStage === targetCloseStage) {
        return block('already-there');
      }
      if (targetCloseStage == null && currentCloseStage != null && currentCloseStage > 0) return { allowed: true };
      return isCloseStageConfigured(configuredCloseStageCount, targetCloseStage)
        ? { allowed: true }
        : block('stage-not-configured');

    // §4.8 — un-close, change nothing else. Only reachable from `closed`, and
    // (BL-2278) only for an auction that opened: clearing `closed_at` on one
    // that never did lands it in Draft / Upcoming / Accepting pre-bids without
    // the go-live gate — the side door BL-1559 closed on `mark-pre-bidding`.
    //
    // BL-2280 — the stage fence answers before `never-opened` (gate decision,
    // Michael Lawrie 2026-10-07): moving back to plain Closed is the first fix
    // either way, and `never-opened`'s copy would offer Draft / Upcoming, which
    // are fenced too.
    case 'reopen':
      if (kind !== 'closed') return block('not-reopenable');
      if (staged) return block('close-stage-set');
      return hasOpened ? { allowed: true } : block('never-opened');

    // §4.9 — any non-deleted phase, subject to the SoftDeleteService restrict
    // blockers the caller resolved for us (RAC 2: the blocker list is still
    // SoftDeleteService's, this guard only reads its verdict).
    //
    // BL-2280 (RAC 3) — the stage fence outranks the restrict blockers: the lots
    // and bids are not what stands in the way of a staged auction's delete.
    case 'soft-delete':
      if (staged) return block('close-stage-set');
      return softDeleteBlocked ? block('delete-blocked') : { allowed: true };

    // Unreachable as of BL-1292 — every member of AuctionTransition has a case
    // above. Kept because it is what makes `tsc` prove that: adding a transition
    // to the union without deciding it here is a compile error, not a silent
    // `undefined`. If a future transition is declared but not yet built, leaving
    // it out of IMPLEMENTED_TRANSITIONS routes it to a loud throw rather than a
    // phase-shaped guess.
    default:
      throw new TransitionNotImplementedError(transition, 'canApplyAuctionTransition');
  }
}

/**
 * BL-2281 — does the house name the stage a `set-close-stage` targets? With no
 * target (the check path's ask) the question is whether the house names any
 * stage at all. With no count, there is no house bound.
 *
 * @param configured - the house's named stage count, or undefined for no bound.
 * @param target - the target stage, or undefined for the target-less ask.
 * @returns true when the move stays inside the house's named stages.
 */
function isCloseStageConfigured(configured: number | undefined, target: number | undefined): boolean {
  if (configured == null) return true;
  if (target == null) return configured > 0;
  return target <= configured;
}

/**
 * Every transition {@link canApplyAuctionTransition} decides today, in the order
 * the matrix §4.0 grid presents them. The check API iterates this so adding a
 * transition to the guard surfaces it in the API without a second edit.
 */
export const DECIDED_AUCTION_TRANSITIONS: readonly AuctionTransition[] = [
  'mark-draft',
  'mark-ready',
  'open-pre-bidding',
  'mark-pre-bidding',
  'open',
  'pause',
  'resume',
  'close',
  'set-close-stage',
  'reopen',
  'soft-delete',
  'restore',
] as const;
